import * as React from 'react';
import styled from 'styled-components';
import strings from '../../strings/strings.json';
import { BodyRegular, FlexCol, FlexColCenter } from '../Elements';
import { useNavigate } from 'react-router-dom';
import { sizes, theme } from 'theme';
import { IconSize } from 'assets/icons/icon-sizes';
import PreviousIcon from 'assets/icons/controls/PreviousIcon';

type StepNavigationProps = {
    title: string;
    aria: string;
    href?: string;
    navigateState?: any;
    asBackButton?: boolean; // use router.back()
    buttonFunctionOverride?: Function;
};

export function StepNavigation({
    title,
    aria,
    href,
    navigateState,
    asBackButton,
    buttonFunctionOverride,
}: StepNavigationProps): JSX.Element {
    const navigate = useNavigate();

    function navigateBack(): void {
        navigate(-1);
    }

    function navigateTo(href: string): void {
        navigate(href, navigateState);
    }

    function buttonFunction() {
        if (buttonFunctionOverride) {
            buttonFunctionOverride();
            return;
        }

        if (asBackButton) {
            navigateBack();
            return;
        } else {
            navigateTo(href ?? '');
            return;
        }
    }

    return (
        <StepNavigatorContainer aria-label={aria} onClick={buttonFunction}>
            <FlexColCenter>
                <PreviousIcon size={IconSize.LARGE} />
            </FlexColCenter>
            <StepNavigatorText>
                {title.length === 0 ? (
                    <ButtonText>{strings.stepNavigation.back}</ButtonText>
                ) : (
                    <ButtonText>{`${strings.stepNavigation.backTo} ${title}`}</ButtonText>
                )}
            </StepNavigatorText>
        </StepNavigatorContainer>
    );
}

const StepNavigatorContainer = styled.button`
    all: unset;
    display: flex;
    justify-content: flex-start;
    gap: ${sizes.spacingSm};
    align-items: center;
    padding: ${sizes.spacingXs};
    cursor: pointer;
    border: 1px solid transparent;
    background-color: inherit;

    &:focus {
        background-color: ${theme.buttonFocusBackgroundColour};
        border: 1px solid ${theme.primaryButtonBorderColour};
        outline: ${theme.primaryButtonBorderColour};
        color: ${theme.buttonFocusTextColour};
        border-radius: 4px;
    }

    &:hover {
        background-color: ${theme.secondaryButtonHoverBackgroundColour};
        border-radius: 4px;
    }
`;

const StepNavigatorText = styled(FlexCol)`
    font-weight: bold;
`;

const ButtonText = styled(BodyRegular)`
    font-weight: bold;
    white-space: nowrap;
    color: ${theme.linkColour};
`;
